import React, { useEffect, useMemo, useState } from "react";
import { checkPseudoAvailability } from "../../api/pseudoService";
import { getAnon } from "../../utils/localStorage";

const defaultInputClassName =
  "w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20";

function normalizePseudo(value) {
  return String(value || "").trim().replace(/\s+/g, " ");
}

export default function PseudoField({
  value,
  onChange,
  label = "Pseudo",
  placeholder = "Ton pseudo",
  inputClassName = defaultInputClassName,
  helperClassName = "text-xs text-slate-500",
  disabled = false,
  required = true,
  onAvailabilityChange,
}) {
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [suggestedPseudo, setSuggestedPseudo] = useState("");

  const normalizedValue = useMemo(() => normalizePseudo(value), [value]);

  useEffect(() => {
    if (!normalizedValue) {
      setStatus("idle");
      setMessage("");
      setSuggestedPseudo("");
      onAvailabilityChange?.({ available: false, pseudo: normalizedValue });
      return undefined;
    }

    if (normalizedValue.length < 3) {
      setStatus("invalid");
      setMessage("Choisis au moins 3 caracteres.");
      setSuggestedPseudo("");
      onAvailabilityChange?.({ available: false, pseudo: normalizedValue });
      return undefined;
    }

    if (normalizedValue.length > 21) {
      setStatus("invalid");
      setMessage("Le pseudo ne peut pas dépasser 21 caractères.");
      setSuggestedPseudo("");
      onAvailabilityChange?.({ available: false, pseudo: normalizedValue });
      return undefined;
    }

    const controller = new AbortController();
    const timeout = window.setTimeout(async () => {
      setStatus("checking");
      setMessage("Verification du pseudo...");
      setSuggestedPseudo("");

      try {
        const anon = getAnon();
        const data = await checkPseudoAvailability({
          pseudo: normalizedValue,
          uuid: anon?.uuid,
          signal: controller.signal,
        });

        if (data.available) {
          setStatus("available");
          setMessage("Pseudo disponible.");
          setSuggestedPseudo("");
          onAvailabilityChange?.({ available: true, pseudo: normalizedValue });
        } else {
          setStatus("taken");
          setMessage("Ce pseudo est deja utilise.");
          setSuggestedPseudo(data.suggested_pseudo || "");
          onAvailabilityChange?.({
            available: false,
            pseudo: normalizedValue,
            suggestedPseudo: data.suggested_pseudo || "",
          });
        }
      } catch (error) {
        if (error.name === "CanceledError" || error.name === "AbortError") {
          return;
        }
        setStatus("error");
        setMessage("Verification indisponible pour le moment.");
        setSuggestedPseudo("");
        onAvailabilityChange?.({ available: false, pseudo: normalizedValue });
      }
    }, 350);

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [normalizedValue, onAvailabilityChange]);

  const statusClassName =
    status === "available"
      ? "text-emerald-600"
      : status === "taken" || status === "invalid" || status === "error"
        ? "text-rose-600"
        : "text-slate-500";

  return (
    <div className="space-y-2">
      {label ? (
        <label className="block text-sm font-semibold text-inherit">
          {label}
        </label>
      ) : null}
      <input
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className={inputClassName}
        disabled={disabled}
        required={required}
        minLength={3}
        maxLength={21}
      />
      {message ? (
        <div className={`${helperClassName} ${statusClassName}`}>{message}</div>
      ) : null}
      {suggestedPseudo ? (
        <button
          type="button"
          onClick={() => onChange(suggestedPseudo)}
          className="rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-xs font-semibold text-cyan-700 transition hover:bg-cyan-100"
        >
          Utiliser {suggestedPseudo}
        </button>
      ) : null}
    </div>
  );
}
